import { put } from 'redux-saga/effects';
import errorHandler from 'utils/errorHandler';
import { PAYMENT_BASE_URL } from 'constants/env';
import toaster from 'utils/toaster';
import {
  docusignInitiateStart,
  docusignInitiateSuccess,
  docusignInitiateFail,
  docusignGetEnvelopeStart,
  docusignGetEnvelopeSuccess,
  docusignGetEnvelopeFail,
} from '../../actions';

/**
 * Saga: POST /payment/docusign/initiate
 * Creates a DocuSign envelope and retrieves the embedded signing URL.
 *
 * payload: {
 *   requestBody: { propertyId, propertyName, propertyCity, propertyState,
 *                  tokens, pricePerToken, amount, currency, pmEmail, pmName },
 *   handleSuccess: (data) => void  ← called with { envelopeId, signingUrl }
 *   handleFail: (error) => void    ← called on API error (optional)
 * }
 */
export function* initiateDocuSignSaga({ payload }) {
  const { requestBody, handleSuccess, handleFail } = payload;
  yield put(docusignInitiateStart());

  yield errorHandler({
    endpoint: '/docusign/initiate',
    baseURL: PAYMENT_BASE_URL,
    successHandler: yield function* (response) {
      const { data } = response;
      yield put(docusignInitiateSuccess(data));
      if (handleSuccess) handleSuccess(data);
    },
    failHandler: yield function* (response) {
      yield put(docusignInitiateFail(response));
      toaster.error(response || 'Failed to initiate document signing. Please try again.');
      if (handleFail) handleFail(response);
    },
    payload: requestBody,
    apiType: 'post',
    token: true,
    failHandlerType: 'CUSTOM',
  });
}

/**
 * Saga: GET /payment/docusign/envelope/:envelopeId
 * Fetches the current status of a DocuSign envelope from our backend DB.
 *
 * payload: {
 *   envelopeId: string,
 *   handleSuccess: (data) => void  ← called with envelope record
 * }
 */
export function* getDocuSignEnvelopeSaga({ payload }) {
  const { envelopeId, handleSuccess } = payload;
  yield put(docusignGetEnvelopeStart());

  yield errorHandler({
    endpoint: `/docusign/envelope/${envelopeId}`,
    baseURL: PAYMENT_BASE_URL,
    successHandler: yield function* (response) {
      const { data } = response;
      yield put(docusignGetEnvelopeSuccess(data));
      if (handleSuccess) handleSuccess(data);
    },
    failHandler: yield function* (response) {
      yield put(docusignGetEnvelopeFail(response));
      toaster.error(response || 'Failed to fetch document status.');
    },
    payload: '',
    apiType: 'get',
    token: true,
    failHandlerType: 'CUSTOM',
  });
}
